import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const RamsUploadPanel = ({ sites, onUpload, uploading, readOnly = false }) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  const validateFile = (f) => {
    if (!ALLOWED_TYPES?.includes(f?.type)) {
      return 'Only PDF and Word documents are supported.';
    }
    if (f?.size > 52428800) {
      return 'File must be under 50MB.';
    }
    return null;
  };

  const handleFileSelect = (f) => {
    const err = validateFile(f);
    if (err) { setError(err); return; }
    setError('');
    setFile(f);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const f = e?.dataTransfer?.files?.[0];
    if (f) handleFileSelect(f);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setError('');
    if (!title?.trim()) { setError('Please enter a document title.'); return; }
    if (!selectedSiteId) { setError('Please select a site.'); return; }
    if (!file) { setError('Please select a file to upload.'); return; }
    onUpload({ title: title?.trim(), siteId: selectedSiteId, file });
    setTitle('');
    setSelectedSiteId('');
    setFile(null);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="Upload" size={18} className="text-accent" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">{t('ramsUploadTitle')}</h2>
          <p className="text-xs text-muted-foreground">{t('ramsUploadSubtitle')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('ramsDocumentTitle')} <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e?.target?.value)}
            placeholder="e.g. Excavation Method Statement v2"
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
          />
        </div>

        {/* Site Selector */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('site')} <span className="text-destructive">*</span>
          </label>
          <select
            value={selectedSiteId}
            onChange={(e) => setSelectedSiteId(e?.target?.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
          >
            <option value="">{t('ramsSelectSite')}</option>
            {sites?.map((site) => (
              <option key={site?.id} value={site?.id}>{site?.site_name}</option>
            ))}
          </select>
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('ramsDocumentFile')} <span className="text-destructive">*</span>
          </label>
          <div
            onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef?.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-accent bg-accent/5'
                : file
                ? 'border-success bg-success/5' :'border-border hover:border-accent/50 hover:bg-muted/30'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => { const f = e?.target?.files?.[0]; if (f) handleFileSelect(f); }}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <Icon name="FileCheck" size={22} className="text-success" />
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground truncate max-w-[200px]">{file?.name}</p>
                  <p className="text-xs text-muted-foreground">{(file?.size / 1024 / 1024)?.toFixed(2)} MB</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e?.stopPropagation(); setFile(null); }}
                  className="ml-2 text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            ) : (
              <div>
                <Icon name="CloudUpload" size={28} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-foreground font-medium">{t('ramsDragDropHint')}</p>
                <p className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX</p>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-lg px-3 py-2">
            <Icon name="AlertCircle" size={15} />
            <span>{error}</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={uploading || readOnly}
          className="w-full"
        >
          {uploading ? (
            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />{t('ramsUploading')}</>
          ) : (
            <><Icon name="Upload" size={16} className="mr-2" />{t('ramsUploadButton')}</>
          )}
        </Button>
      </form>
    </div>
  );
};

export default RamsUploadPanel;
